import fs from 'fs-extra';
import path from 'path';
import { SplashConfig } from '../types/config';

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

export async function validateConfig(config: SplashConfig): Promise<void> {
  // Validate image files exist
  await validateImagePath(config.image, 'image');
  await validateImagePath(config.imageAndroid, 'imageAndroid');
  await validateImagePath(config.imageIos, 'imageIos');
  await validateImagePath(config.imageWeb, 'imageWeb');
  await validateImagePath(config.darkImage, 'darkImage');
  await validateImagePath(config.darkImageAndroid, 'darkImageAndroid');
  await validateImagePath(config.darkImageIos, 'darkImageIos');
  await validateImagePath(config.darkImageWeb, 'darkImageWeb');

  // Validate branding images
  await validateImagePath(config.branding, 'branding');
  await validateImagePath(config.brandingAndroid, 'brandingAndroid');
  await validateImagePath(config.brandingIos, 'brandingIos');
  await validateImagePath(config.brandingWeb, 'brandingWeb');
  await validateImagePath(config.brandingDark, 'brandingDark');
  await validateImagePath(config.brandingDarkAndroid, 'brandingDarkAndroid');
  await validateImagePath(config.brandingDarkIos, 'brandingDarkIos');
  await validateImagePath(config.brandingDarkWeb, 'brandingDarkWeb');

  // Validate background images
  await validateImagePath(config.backgroundImage, 'backgroundImage');
  await validateImagePath(config.backgroundImageAndroid, 'backgroundImageAndroid');
  await validateImagePath(config.backgroundImageIos, 'backgroundImageIos');
  await validateImagePath(config.backgroundImageWeb, 'backgroundImageWeb');
  await validateImagePath(config.darkBackgroundImage, 'darkBackgroundImage');
  await validateImagePath(config.darkBackgroundImageAndroid, 'darkBackgroundImageAndroid');
  await validateImagePath(config.darkBackgroundImageIos, 'darkBackgroundImageIos');
  await validateImagePath(config.darkBackgroundImageWeb, 'darkBackgroundImageWeb');

  // Validate Android 12 images
  if (config.android12) {
    await validateImagePath(config.android12.image, 'android12.image');
    await validateImagePath(config.android12.darkImage, 'android12.darkImage');
    await validateImagePath(config.android12.branding, 'android12.branding');
    await validateImagePath(config.android12.brandingDark, 'android12.brandingDark');
  }

  // Validate colors
  validateColor(config.color, 'color');
  validateColor(config.colorAndroid, 'colorAndroid');
  validateColor(config.colorIos, 'colorIos');
  validateColor(config.colorWeb, 'colorWeb');
  validateColor(config.darkColor, 'darkColor');
  validateColor(config.darkColorAndroid, 'darkColorAndroid');
  validateColor(config.darkColorIos, 'darkColorIos');
  validateColor(config.darkColorWeb, 'darkColorWeb');

  if (config.android12) {
    validateColor(config.android12.color, 'android12.color');
    validateColor(config.android12.darkColor, 'android12.darkColor');
    validateColor(config.android12.iconBackgroundColor, 'android12.iconBackgroundColor');
    validateColor(config.android12.iconBackgroundColorDark, 'android12.iconBackgroundColorDark');
  }

  // Validate Info.plist files for iOS
  if (config.infoPlistFiles) {
    for (const plistFile of config.infoPlistFiles) {
      if (!(await fs.pathExists(plistFile))) {
        throw new ValidationError(`Info.plist file not found: ${plistFile}`);
      }
    }
  }

  // Validate at least one platform is enabled
  if (config.android === false && config.ios === false && config.web === false) {
    throw new ValidationError('At least one platform (android, ios, web) must be enabled');
  }
}

async function validateImagePath(
  imagePath: string | undefined,
  parameterName: string
): Promise<void> {
  if (!imagePath) return;

  if (!(await fs.pathExists(imagePath))) {
    throw new ValidationError(
      `Image file not found for parameter "${parameterName}": ${imagePath}`
    );
  }

  const supportedFormats = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'];
  const ext = path.extname(imagePath).toLowerCase();

  if (!supportedFormats.includes(ext)) {
    throw new ValidationError(
      `Unsupported image format for parameter "${parameterName}": ${imagePath}. ` +
        `Supported formats: ${supportedFormats.join(', ')}`
    );
  }
}

function validateColor(color: string | undefined, parameterName: string): void {
  if (!color) return;

  // Remove # if present and validate hex color
  const cleanColor = color.replace('#', '');

  if (!/^[0-9A-Fa-f]{6}$/.test(cleanColor)) {
    throw new ValidationError(
      `Invalid color format for parameter "${parameterName}": ${color}. ` +
        'Expected format: #RRGGBB or RRGGBB'
    );
  }
}
